/**
 * Gift card recipient form.
 *
 *  1. One suggested message per occasion — picking an occasion drops it
 *     straight into the box. A blank textarea mostly stays blank; a seeded
 *     line usually gets rewritten in the buyer's own words, which is the
 *     whole point of the field.
 *  2. The recipient's name appears on the live card as it is typed.
 *  3. Blocks submit when the recipient email is missing, so the buyer finds
 *     out here rather than after a checkout round trip.
 *
 * Deliberately plain custom-element JS with no theme-internal imports, so it
 * cannot break when the theme's component layer changes.
 */

const SUGGESTIONS = {
  'birthday':     ['יום הולדת הוא תירוץ מצוין לתכשיט חדש'],
  'rosh-hashana': ['לשנה של בחירות טובות, גם במה שעונדים'],
  'thanks':       ['תודה – לפעמים תכשיט אומר את זה טוב יותר'],
  'love':         ['משהו ממני – הבחירה לגמרי שלך'],
  'chag-sameach': ['חג הוא סיבה טובה להתחדש'],
};

const EN = {
  'birthday':     ['Another year, another reason to wear something stylish'],
  'rosh-hashana': ["Here's to good choices — including what you wear"],
  'thanks':       ['Because a good thank-you should last'],
  'love':         ['Something from me — the choice is all yours'],
  'chag-sameach': ['A little glam for the holiday'],
};

class GiftCardRecipient extends HTMLElement {
  connectedCallback() {
    const $ = (r) => this.querySelector(`[ref="${r}"]`);
    this.message = $('message');
    this.count = $('count');
    this.idx = $('idx');
    this.total = $('total');
    this.prev = $('prev');
    this.next = $('next');
    this.clear = $('clear');
    this.toName = $('toName');
    this.toEmail = $('toEmail');
    this.emailError = $('emailError');
    this.offset = $('offset');
    this.sendFlag = $('sendFlag');
    this.previewName = $('previewName');
    this.toNameMirror = $('toNameMirror');
    this.previewAmount = $('previewAmount');

    this.form = this.closest('form');
    this.pool = document.documentElement.lang === 'he' ? SUGGESTIONS : EN;
    this.pos = 0;
    /** What we last wrote into the box. Lets us tell our text from theirs. */
    this.lastInserted = '';

    this.addEventListener('change', (e) => {
      if (e.target.name === 'properties[Occasion]') this.onOccasion();
    });
    this.message?.addEventListener('input', () => { this.onMessage(); this.saveState(); });
    this.toName?.addEventListener('input', () => { this.onName(); this.saveState(); });
    this.toEmail?.addEventListener('input', () => { this.clearEmailError(); this.syncSendFlag(); this.saveState(); });
    this.prev?.addEventListener('click', () => this.cycle(-1));
    this.next?.addEventListener('click', () => this.cycle(1));
    this.clear?.addEventListener('click', () => this.clearMessage());

    // Shopify schedules the send using the buyer's own timezone.
    if (this.offset) this.offset.value = String(new Date().getTimezoneOffset() * -1);

    // Capture phase so we run before the theme's own submit handler.
    this.form?.addEventListener('submit', (e) => this.onSubmit(e), true);

    // insert:false — on load we only paint the pager. Writing a suggested
    // message into an untouched form means a buyer who fills in nothing still
    // sends a greeting they never wrote.
    this.onOccasion({ insert: false });
    this.onName();
    this.syncSendFlag();
    this.watchPrice();
    this.saveState();
  }

  /**
   * The flag and the recipient email travel together: Shopify rejects the flag
   * without an email, and ignores the email without the flag. Enabling the
   * hidden input only when a valid address is present means a blank email
   * simply falls back to Shopify's default — the card goes to the buyer.
   */
  syncSendFlag() {
    if (!this.sendFlag) return;
    const email = (this.toEmail?.value ?? '').trim();
    this.sendFlag.disabled = !(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  }

  /**
   * Mirror the selected variant's price onto the card.
   *
   * The theme morphs the product-info DOM on every variant change, so an
   * observer attached to the price node ends up watching a detached element
   * and silently stops firing. The theme's own `variant:update` event is the
   * reliable signal; prices come from a Liquid-formatted map so the card shows
   * exactly what the page shows, in the store's own currency format.
   */
  watchPrice() {
    try {
      this.prices = JSON.parse(this.dataset.prices || '{}');
    } catch {
      this.prices = {};
    }
    this.paintPrice(this.dataset.initialVariant);

    this.onVariantUpdate = (e) => {
      const id = e?.detail?.resource?.id;
      if (id) this.paintPrice(id);
      // Morphing can blank the fields; put the buyer's own text back.
      this.restoreState();
    };
    document.addEventListener('variant:update', this.onVariantUpdate);
  }

  paintPrice(variantId) {
    if (!this.previewAmount || variantId == null) return;
    const price = this.prices?.[String(variantId)];
    if (price) this.previewAmount.textContent = price;
  }

  /** Snapshot of what the buyer typed, so a DOM morph cannot lose it. */
  saveState() {
    this.state = {
      toName: this.toName?.value ?? '',
      fromName: this.querySelector('[ref="fromName"]')?.value ?? '',
      toEmail: this.toEmail?.value ?? '',
      toPhone: this.querySelector('[ref="toPhone"]')?.value ?? '',
      message: this.message?.value ?? '',
      sendOn: this.querySelector('[ref="sendOn"]')?.value ?? '',
    };
  }

  restoreState() {
    if (!this.state) return;
    const put = (ref, v) => {
      const el = this.querySelector(`[ref="${ref}"]`);
      if (el && !el.value && v) el.value = v;
    };
    put('toName', this.state.toName);
    put('fromName', this.state.fromName);
    put('toEmail', this.state.toEmail);
    put('toPhone', this.state.toPhone);
    put('message', this.state.message);
    put('sendOn', this.state.sendOn);
    this.onName();
    this.onMessage();
    this.syncSendFlag();
  }

  disconnectedCallback() {
    if (this.onVariantUpdate) document.removeEventListener('variant:update', this.onVariantUpdate);
  }

  currentList() {
    const picked = this.querySelector('input[name="properties[Occasion]"]:checked');
    // no occasion chosen yet: show the birthday set in the pager, but nothing
    // is written into the box until the buyer actually picks one
    return this.pool[picked ? picked.value : 'birthday'] || [];
  }

  /** Picking an occasion re-seeds the box — unless the buyer wrote their own. */
  onOccasion({ insert = true } = {}) {
    const list = this.currentList();
    this.pos = 0;
    if (this.total) this.total.textContent = String(list.length);
    const own = this.message && this.message.value.trim() && this.message.value !== this.lastInserted;
    if (insert && !own) this.insert(list[0]);
    else this.paint();
  }

  cycle(step) {
    const list = this.currentList();
    if (!list.length) return;
    this.pos = (this.pos + step + list.length) % list.length;
    this.insert(list[this.pos]);
  }

  insert(text) {
    if (!this.message || text == null) return;
    this.message.value = text;
    this.lastInserted = text;
    this.onMessage();
    this.paint();
  }

  clearMessage() {
    if (!this.message) return;
    this.message.value = '';
    this.lastInserted = '';
    this.onMessage();
    this.message.focus();
  }

  paint() {
    if (this.idx) this.idx.textContent = String(this.pos + 1);
  }

  onMessage() {
    if (this.count) this.count.textContent = String((this.message?.value ?? '').length);
  }

  onName() {
    const v = (this.toName?.value ?? '').trim();
    if (this.previewName) this.previewName.textContent = v;
    // survives Shopify consuming the native Recipient name property
    if (this.toNameMirror) this.toNameMirror.value = v;
  }

  clearEmailError() {
    this.toEmail?.setAttribute('aria-invalid', 'false');
    if (this.emailError) this.emailError.hidden = true;
  }

  /**
   * The email is optional. Blank is fine — the card then goes to the buyer.
   * We only block on a value that was typed but is not a valid address, so a
   * typo cannot silently send the gift to nobody.
   */
  onSubmit(e) {
    const email = (this.toEmail?.value ?? '').trim();
    const valid = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (valid) return;

    e.preventDefault();
    e.stopPropagation();
    this.toEmail?.setAttribute('aria-invalid', 'true');
    if (this.emailError) {
      this.emailError.textContent =
        this.emailError.dataset.message || 'Please enter the recipient’s email';
      this.emailError.hidden = false;
    }
    this.toEmail?.focus();
    this.toEmail?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

}

if (!customElements.get('gift-card-recipient')) {
  customElements.define('gift-card-recipient', GiftCardRecipient);
}
